import { ReactNode } from "react";
import { CircleCheck, CircleX, Info, TriangleAlert } from "lucide-react";

type Tone = "ok" | "warn" | "error" | "info";

// Looked up by tone: the icon, and the box and icon colours that go with it.
const TONES = {
  ok: {
    Icon: CircleCheck,
    box: "border-green-200 bg-green-50",
    icon: "text-green-600",
  },
  warn: {
    Icon: TriangleAlert,
    box: "border-amber-200 bg-amber-50",
    icon: "text-amber-600",
  },
  error: {
    Icon: CircleX,
    box: "border-red-200 bg-red-50",
    icon: "text-red-600",
  },
  info: {
    Icon: Info,
    box: "border-indigo-200 bg-indigo-50",
    icon: "text-indigo-600",
  },
};

type StatusBannerProps = {
  tone: Tone;
  title: string;
  detail?: ReactNode;
  /** Right side of the banner, e.g. a button that fixes the problem. */
  action?: ReactNode;
};

export function StatusBanner({
  tone,
  title,
  detail,
  action,
}: StatusBannerProps) {
  const { Icon, box, icon } = TONES[tone];

  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={`flex flex-wrap items-start gap-3 rounded-xl border p-4 ${box}`}
    >
      <Icon aria-hidden className={`mt-0.5 size-5 shrink-0 ${icon}`} />
      <div className="min-w-0 flex-1 basis-64">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        {detail && <div className="mt-0.5 text-sm text-gray-700">{detail}</div>}
      </div>
      {action}
    </div>
  );
}
